import { Controller } from "@hotwired/stimulus"

// Base for the delegated overlay controllers (modal, drawer), attached to
// <body>. Any element on the page can drive an overlay with
//
//   data-action="modal#toggle" data-modal-id-param="my-modal"
//
// The Flowbite instance is created lazily, on first use, from whatever
// element currently has that id — so the trigger, the overlay and its close
// buttons can arrive in different Turbo frames or stream actions without
// having to be initialized together. There is one instance per overlay
// element, keyed by its id with override: true, because an id names exactly
// one overlay.
//
// Subclasses implement _create(element, id) and return the Flowbite instance.
export default class extends Controller {
  connect() {
    this.instances = new Map()
    this._sweep = this._sweep.bind(this)
    this._sweepAfterStreamRender = this._sweepAfterStreamRender.bind(this)
    document.addEventListener("turbo:frame-render", this._sweep)
    document.addEventListener("turbo:render", this._sweep)
    document.addEventListener(
      "turbo:before-stream-render",
      this._sweepAfterStreamRender,
    )
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this._sweep)
    document.removeEventListener("turbo:render", this._sweep)
    document.removeEventListener(
      "turbo:before-stream-render",
      this._sweepAfterStreamRender,
    )
    this._hideAll()
    this.instances.forEach((instance) => instance.destroyAndRemoveInstance())
    this.instances.clear()
  }

  toggle({ params: { id } }) {
    this._instance(id)?.toggle()
  }

  show({ params: { id } }) {
    this._instance(id)?.show()
  }

  hide({ params: { id } }) {
    this._instance(id)?.hide()
  }

  _hideAll() {
    this.instances.forEach((instance) => {
      if (instance.isVisible()) instance.hide()
    })
  }

  _instance(id) {
    this._sweep()

    const element = document.getElementById(id)
    if (!element) return null

    let instance = this.instances.get(element)
    if (!instance) {
      instance = this._create(element, id)
      this.instances.set(element, instance)
    }
    return instance
  }

  // Turbo exposes no after-stream-render event; wrap the render callback so
  // the sweep runs as soon as the stream has touched the DOM.
  _sweepAfterStreamRender(event) {
    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      this._sweep()
    }
  }

  // Forget instances whose element was re-rendered out of the DOM, hiding
  // them first so an orphaned backdrop never lingers. Runs after every Turbo
  // render and before every action, so a frame or stream that replaces an
  // open overlay clears its backdrop immediately rather than on the next
  // click.
  _sweep() {
    this.instances.forEach((instance, element) => {
      if (element.isConnected) return
      if (instance.isVisible()) instance.hide()
      instance.destroyAndRemoveInstance()
      this.instances.delete(element)
    })
  }
}
